import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { BaseAddressModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import CustomerCard, { CustomerCardStyles } from "@insite/content-library/Components/CustomerCard";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import styled, { css } from "styled-components";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import translate from "@insite/client-framework/Translate";

interface OwnProps {
    customers: BaseAddressModel[];
    pagination: PaginationModel | null;
    selectedCustomer?: BaseAddressModel;
    onSelect: (customer: BaseAddressModel) => void;
    /** @deprecated Not used anymore */
    allowEditCustomer?: boolean;
    onEdit?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => void;
    onChangePage: (page: number) => void;
    onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    extendedStyles?: CustomerSelectorStyles;
    customerSelectorModalCloseHandler: () => void;
}

export interface CustomerSelectorStyles {
    customerCard?: CustomerCardStyles;
    customerCardSelected?: CustomerCardStyles;
    pagination?: PaginationPresentationProps; 
    paginationWrapper?: InjectableCss;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalContinueButton?: ButtonPresentationProps; 
}

const linkTextStyles: TypographyPresentationProps = {
    color: "text.link",
};

export const customerSelectorStyles: CustomerSelectorStyles = {
    customerCardSelected: {
        address: {
            attentionText: linkTextStyles,
            customerNameText: linkTextStyles,
            companyNameText: linkTextStyles,
            address1Text: linkTextStyles,
            address2Text: linkTextStyles,
            address3Text: linkTextStyles,
            address4Text: linkTextStyles,
            cityStatePostalCodeText: linkTextStyles,
            countryText: linkTextStyles,
            phoneText: linkTextStyles,
            emailText: linkTextStyles,
            faxText: linkTextStyles,
        },
    },
    paginationWrapper:{
        css: css`
            padding-left: 25px;

            span[class^='PerPageSelect'], span[class*=' PerPageSelect'] {
                display: none;
            }
            nav[data-test-selector="paginationCurrentPage1"],
            nav[role="navigation"] {
                display: block;
                width: 100%;
                max-width: 70%;
                margin: 0 auto;
                text-align: center;
                margin-top: 1.875rem;
                margin-bottom: 1.875rem;

                .pagination-arrow {
                    button {
                        height: 1.25rem;
                    }
                }
            }
            .btn-wrapper {
                border-radius: 1px;
                box-shadow: 0 -2px 6px 0 rgba(0,0,0,0.31);
                background-color: #fff;
                margin-top: 1.875rem;
                padding-bottom: 1.875rem;
                padding-top: 0;
                position: fixed;
                width: 570px;
                left: auto;
                bottom: 0px;
                right: 0;

                @media screen and (max-width: 767px) {
                    width: 100%;
                    box-shadow: none;
    
                    > button[data-test-selector="customerAddressForm_save"] {
                        margin-bottom: 0;
                    }
                }
            }
            .cancel-btn {
                position: fixed;
                bottom: 1.8rem;
                left: auto;
                right: 410px;
                color: #868686 !important;
            }
        `,
    },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 70%;
            margin: 0 auto;
            justify-content: center;
            align-items: end;
        `,
    },
    addressBookModalContinueButton: {
        variant: "primary",
        css: css`
            background-color: #002554;
            border-color: #002554;
            color: #fff;
            text-transform: uppercase;
            padding: 0 60px;
            width: auto;
            border-radius: 2px;

            &:hover,
            &:active, 
            &:focus {
                background-color: #002554 !important;
                border-color: #002554 !important;
                color: #fff;
            }
        `,
    },
};

const CustomerSelector = ({
    customers,
    pagination,
    extendedStyles,
    selectedCustomer,
    onChangePage,
    onChangeResultsPerPage,
    onSelect,
    onEdit,
    customerSelectorModalCloseHandler,
}: OwnProps) => {
    const [styles] = useState(() => mergeToNew(customerSelectorStyles, extendedStyles));

    return (
        <>
            <StyledWrapper {...styles.paginationWrapper}>
                {customers.map(customer => {
                    const isCardSelected = customer.id === selectedCustomer?.id;
                    return (
                        <CustomerCard
                            key={customer.id}
                            customer={customer}
                            isSelected={isCardSelected}
                            extendedStyles={isCardSelected ? styles.customerCardSelected : styles.customerCard}
                            onSelect={onSelect}
                            onEdit={onEdit}
                        />
                    );
                })}
                
                <StyledWrapper className="btn-wrapper">
                    {customers.length > 0 && pagination && (
                        <Pagination
                            {...styles.pagination}
                            currentPage={pagination.currentPage}
                            resultsPerPage={pagination.pageSize}
                            resultsCount={pagination.totalItemCount}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={onChangePage}
                            onChangeResultsPerPage={onChangeResultsPerPage}
                        />
                    )}
                
                    <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                        <Button
                            {...styles.addressBookModalContinueButton}
                            data-test-selector="addressModal_continue"
                            onClick={customerSelectorModalCloseHandler}
                        >
                            {translate("Continue")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

        </>
    );
};

export default CustomerSelector;
